import { NgClass } from "@angular/common";
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostListener,
    Input,
    inject
} from "@angular/core";
import { debounce } from "lodash";
import { ObjectUtils } from "~app/common/utils/object-utils";

@Component({
    selector: "rask-text-expander",
    styleUrls: ["./text-expander.component.scss"],
    templateUrl: "./text-expander.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [NgClass]
})

export class TextExpanderComponent implements AfterViewInit {
    exceedsMaxNumRows = false;
    expanded = false;

    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input("rows") numberOfRows = 2;

    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input("text") textInput: string;

    private readonly elementRef = inject(ElementRef);

    private debounced: any;
    private maxHeight: number;
    private messageElement: HTMLElement;

    get textClass(): string {
        if (!this.exceedsMaxNumRows) {
            return "";
        }

        return this.expanded
            ? "expanded"
            : "fade-text";
    }

    get toggleText(): string {
        return this.expanded ? "Visa mindre" : "Visa mer";
    }

    ngAfterViewInit(): void {
        this.messageElement = this.elementRef.nativeElement.querySelector(".text-wrapper");

        setTimeout(() => {
            const color = this.getBackgroundColorFromAncestor(this.messageElement);

            if (color) {
                const values = this.toRgb(color);
                if (values) {
                    this.messageElement.style.setProperty("--gradient-stop", `rgba(${values.red}, ${values.green}, ${values.blue}, ${values.alpha})`);
                }
            }

            this.setHeightStyles();
        }, 0);
    }

    @HostListener("window:resize", ["$event.target"])
    onResize(event: any) {
        if (this.debounced) {
            this.debounced.cancel();
        }

        this.debounced = debounce(() => {
            this.setHeightStyles();
            this.debounced = undefined;
        }, 200);

        this.debounced();
    }

    toggle(): void {
        this.expanded = !this.expanded;
        if (this.expanded) {
            this.messageElement.style.height = "";
        } else {
            this.messageElement.style.height = `${this.maxHeight}px`;
        }
    }

    private getBackgroundColorFromAncestor(element: HTMLElement): string {
        const computedStyle = window.getComputedStyle(element);
        if (this.hasColorValue(computedStyle.background)) {
            return computedStyle.background;
        }

        if (this.hasColorValue(computedStyle.backgroundColor)) {
            return computedStyle.backgroundColor;
        }

        if (!element.parentElement) {
            return null;
        }

        return this.getBackgroundColorFromAncestor(element.parentElement);
    }

    private hasColorValue(value: string): boolean {
        if (!ObjectUtils.stringHasValue(value)) {
            return false;
        }

        if (value === "transparent") {
            return false;
        }

        if (value.indexOf("rgba(0, 0, 0, 0)") > -1) {
            return false;
        }

        return true;
    }

    private setHeightStyles(): void {
        const height = Math.max(this.messageElement.scrollHeight, this.messageElement.offsetHeight);
        const style = window.getComputedStyle(this.messageElement);
        const lineHeight = parseInt(style.lineHeight, 10);
        const rows = Math.ceil(height / lineHeight);

        if (rows > this.numberOfRows) {
            this.exceedsMaxNumRows = true;
            this.maxHeight = lineHeight * this.numberOfRows;
            this.messageElement.style.height = `${this.maxHeight}px`;
            this.messageElement.style.setProperty("--line-height", `${lineHeight}px`);

            setTimeout(() => {
                const showMoreElement = this.elementRef.nativeElement.querySelector(".show-more");
                if (showMoreElement) {
                    showMoreElement.style.setProperty("--alignment-bottom", `-${lineHeight}px`);
                }
            }, 0);

        } else {
            this.exceedsMaxNumRows = false;
            this.maxHeight = undefined;
            this.messageElement.style.height = "";
        }
    }

    private toRgb(str: string) {
        // eslint-disable-next-line sonarjs/prefer-regexp-exec
        const match = str.match(/rgba?\((\d{1,3}), ?(\d{1,3}), ?(\d{1,3})\)?(?:, ?(\d(?:\.\d?))\))?/);
        if (!match) {
            return null;
        }

        return {
            red: match[1],
            green: match[2],
            blue: match[3],
            alpha: match[4] || "1"
        };
    }
}
